import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ReimbursementDetailsContent } from "./ReimbursementDetailsContent";
import {
  AddExpenseToReportMutationVariables,
  ExpenseReportDocument,
  ExpenseReportsByViewDocument,
  useAddExpenseToReportMutation,
} from "@common/utils/graphql";
import { format } from "date-fns";
import { idempotencyKeyFromObject, receiptInputData } from "@/utils/utils";
import { toast } from "@/components/ui/use-toast";
import { expenseFormSchemaObject } from "../forms/Forms";
import { sanatizeCustomFieldEntries } from "@common/components/CustomFields/utils";
import { useCustomFieldFormConfig } from "@common/components/CustomFields";
import { z } from "zod";

type Props = {
  onCloseView: () => void;
  reportId: number;
};

// This is used when you click on Edit Report => Add Expense
export const AddExpenseToReport = ({ onCloseView, reportId }: Props) => {
  const [addExpenseToReport, { loading }] = useAddExpenseToReportMutation();
  const { customFieldSchema, customFieldDefaultValues } =
    useCustomFieldFormConfig();
  const schema = z.object({
    ...expenseFormSchemaObject,
    ...customFieldSchema,
  });

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      id: undefined,
      merchant: undefined,
      expenseCategoryGLId: undefined,
      amount: undefined,
      date: undefined,
      notes: undefined,
      receipt: undefined,
      reportId: undefined,
      attendeeEmails: undefined,
      ...customFieldDefaultValues,
    },
  });

  const onSubmit = async () => {
    const values = form.getValues();

    const [error, receiptFileData] = await receiptInputData(values.receipt);
    if (error) return;

    const customFieldEntries = sanatizeCustomFieldEntries(
      values.customFieldEntries,
    );
    const params: Omit<AddExpenseToReportMutationVariables, "idempotencyKey"> =
      {
        reportId,
        expenseCategoryGLId: values.expenseCategoryGLId
          ? Number(values.expenseCategoryGLId)
          : undefined,
        merchantName: values.merchant,
        amount: Number(values.amount),
        notes: values.notes || "",
        transactionDate: format(values.date, "yyyy-MM-dd"),
        receiptFileData: receiptFileData,
        attendeeEmails: values.attendeeEmails,
        transactionId: values.transactionId,
        customFieldEntries,
      };
    try {
      const { data } = await addExpenseToReport({
        variables: {
          ...params,
          idempotencyKey: idempotencyKeyFromObject(params),
        },
        refetchQueries: [ExpenseReportDocument, ExpenseReportsByViewDocument],
      });
      if (
        data?.addExpenseToReport.__typename !==
        "MutationAddExpenseToReportSuccess"
      ) {
        throw new Error();
      }
      onCloseView();
    } catch {
      toast({
        variant: "destructive",
        title: "Failed to add expense to report, please try again",
      });
    }
  };

  return (
    <ReimbursementDetailsContent
      onCloseView={onCloseView}
      form={form}
      loading={loading}
      nextStep={onSubmit}
      submitText={loading ? "Saving" : "Save"}
    />
  );
};
